/**
 * Guidance Gantt tree UI helpers (expand/collapse defaults, completed filter).
 */

import type { ControlBridgeQueueItem } from '../deriveViewModel';
import { rollupCounts, statusFromPhrase } from './derivation';
import {
  applyEligibleQueueScheduling,
  envelopeWeekRange,
  weekRangeFromQueueItem,
} from './guidanceSchedule';
import type { GuidanceRollupCounts, GuidanceTreeNode } from './types';

const COLLAPSIBLE_TYPES = new Set<GuidanceTreeNode['type']>(['session', 'waypoint', 'improvement']);

export function isGuidanceNodeCollapsible(node: GuidanceTreeNode): boolean {
  if (!COLLAPSIBLE_TYPES.has(node.type)) {
    return false;
  }
  if (node.children && node.children.length > 0) {
    return true;
  }
  if (node.type === 'improvement' && node.counts) {
    const { done, open, eligible } = node.counts;
    return done + open + eligible > 0;
  }
  return false;
}

function rollupFromChildren(children: GuidanceTreeNode[]): GuidanceRollupCounts {
  let done = 0;
  let open = 0;
  let eligible = 0;
  for (const child of children) {
    if (child.counts) {
      done += child.counts.done;
      open += child.counts.open;
      eligible += child.counts.eligible;
    } else if (child.type === 'activity') {
      if (child.status === 'done') done += 1;
      else if (child.status === 'active') open += 1;
      else eligible += 1;
    } else if (child.children?.length) {
      const sub = rollupFromChildren(child.children);
      done += sub.done;
      open += sub.open;
      eligible += sub.eligible;
    }
  }
  return { done, open, eligible };
}

/** Remove done activities; drop empty done parents; recompute rollup counts. */
export { applyEligibleQueueScheduling } from './guidanceSchedule';

export function filterGuidanceTreeHideCompleted(root: GuidanceTreeNode): GuidanceTreeNode {
  const walk = (node: GuidanceTreeNode): GuidanceTreeNode | null => {
    if (node.type === 'activity') {
      return node.status === 'done' ? null : node;
    }

    const kids = (node.children ?? [])
      .map((child) => walk(child))
      .filter((child): child is GuidanceTreeNode => child != null);

    if (node.type !== 'session' && node.status === 'done' && kids.length === 0) {
      return null;
    }

    const counts =
      node.counts || isGuidanceNodeCollapsible(node) ? rollupFromChildren(kids) : undefined;

    return {
      ...node,
      children: kids.length > 0 ? kids : undefined,
      counts,
    };
  };

  return walk(root) ?? { ...root, children: [] };
}

function collectAllActivityIds(root: GuidanceTreeNode): Set<string> {
  const ids = new Set<string>();
  const walk = (node: GuidanceTreeNode) => {
    if (node.type === 'activity') ids.add(node.id);
    (node.children ?? []).forEach(walk);
  };
  walk(root);
  return ids;
}

function findImprovementContext(
  root: GuidanceTreeNode,
  improvementRef: string
): { waypointId?: string; improvementId: string } | null {
  let found: { waypointId?: string; improvementId: string } | null = null;
  const walk = (node: GuidanceTreeNode, waypointId?: string) => {
    if (found) return;
    const nextWaypoint = node.type === 'waypoint' ? node.id : waypointId;
    if (node.type === 'improvement' && node.id === improvementRef) {
      found = { waypointId: nextWaypoint, improvementId: node.id };
      return;
    }
    (node.children ?? []).forEach((child) => walk(child, nextWaypoint));
  };
  walk(root);
  return found;
}

function resolveEligiblePlacementKey(
  root: GuidanceTreeNode,
  item: ControlBridgeQueueItem
): string {
  if (item.waypointRef) return item.waypointRef;
  if (item.improvementRef) {
    const ctx = findImprovementContext(root, item.improvementRef);
    if (ctx?.waypointId) return ctx.waypointId;
  }
  return '__session__';
}

function appendMissingEligibleActivities(
  root: GuidanceTreeNode,
  eligibleWork: ControlBridgeQueueItem[],
  activityFromQueue: (item: ControlBridgeQueueItem) => GuidanceTreeNode
): GuidanceTreeNode {
  const presentIds = collectAllActivityIds(root);
  const missing = eligibleWork.filter((item) => item.itemId && !presentIds.has(item.itemId));
  if (missing.length === 0) return root;

  const byWaypoint = new Map<string, ControlBridgeQueueItem[]>();
  for (const item of missing) {
    const key = resolveEligiblePlacementKey(root, item);
    const bucket = byWaypoint.get(key) ?? [];
    bucket.push(item);
    byWaypoint.set(key, bucket);
  }

  const attachToWaypoint = (node: GuidanceTreeNode): GuidanceTreeNode => {
    if (node.type === 'waypoint') {
      const bucket = byWaypoint.get(node.id);
      if (bucket?.length) {
        byWaypoint.delete(node.id);
        const grouped = new Map<string, ControlBridgeQueueItem[]>();
        for (const item of bucket) {
          const key = item.improvementRef ?? `${node.id}--eligible-queue`;
          const list = grouped.get(key) ?? [];
          list.push(item);
          grouped.set(key, list);
        }
        let kids = [...(node.children ?? [])];
        Array.from(grouped.entries()).forEach(([impRef, items]) => {
          const existing = kids.find((child) => child.type === 'improvement' && child.id === impRef);
          const actNodes = items.map((item: ControlBridgeQueueItem) => activityFromQueue(item));
          if (existing) {
            kids = kids.map((child) =>
              child.id === impRef
                ? {
                    ...child,
                    children: [...(child.children ?? []), ...actNodes],
                    counts: rollupFromChildren([...(child.children ?? []), ...actNodes]),
                  }
                : child
            );
          } else {
            kids.push({
              id: impRef,
              type: 'improvement',
              label: items[0]?.improvementTitle ?? 'Eligible activities',
              status: 'active',
              range: envelopeWeekRange(actNodes.map((n) => n.range)),
              counts: rollupCounts(actNodes.length, actNodes.length),
              children: actNodes,
            });
          }
        });
        return { ...node, children: kids };
      }
    }
    const kids = (node.children ?? []).map(attachToWaypoint);
    return kids.length > 0 || node.type === 'session' ? { ...node, children: kids } : node;
  };

  let next = attachToWaypoint(root);
  const stillMissing = missing.filter((item) => !collectAllActivityIds(next).has(item.itemId));
  if (stillMissing.length > 0) {
    const actNodes = stillMissing.map((item) => activityFromQueue(item));
    const sessionRange = envelopeWeekRange(actNodes.map((n) => n.range));
    next = {
      ...next,
      children: [
        ...(next.children ?? []),
        {
          id: `${next.id}--eligible-queue`,
          type: 'waypoint',
          label: 'Eligible work',
          status: 'active',
          range: sessionRange,
          counts: rollupCounts(actNodes.length, actNodes.length),
          children: [
            {
              id: `${next.id}--eligible-improvements`,
              type: 'improvement',
              label: 'Attention queue',
              status: 'active',
              range: sessionRange,
              counts: rollupCounts(actNodes.length, actNodes.length),
              children: actNodes,
            },
          ],
        },
      ],
    };
  }
  return next;
}

function isUndecomposedImprovement(node: GuidanceTreeNode): boolean {
  return (
    node.type === 'improvement' &&
    !(node.children ?? []).some((child) => child.type === 'activity')
  );
}

/** Keep session/waypoint/undecomposed-improvement nodes even when no eligible activities remain. */
function keepEligibleOnlyBranch(node: GuidanceTreeNode, childCount: number): boolean {
  if (node.type === 'session') return true;
  if (node.type === 'waypoint' || node.type === 'improvement') {
    return childCount > 0 || isUndecomposedImprovement(node);
  }
  return childCount > 0;
}

function activityNodeFromEligibleQueue(item: ControlBridgeQueueItem): GuidanceTreeNode {
  const status = statusFromPhrase(item.statusPhrase);
  return {
    id: item.itemId,
    type: 'activity',
    label: item.title,
    status,
    range: weekRangeFromQueueItem(item),
    pct: status === 'active' ? 0.55 : 0,
    res: item.assignedResourceLabel,
  };
}

/** Keep activities in the scoped eligible queue (+ inject queue rows missing from the tree). */
export function filterGuidanceTreeEligibleOnly(
  root: GuidanceTreeNode,
  eligibleWork: ControlBridgeQueueItem[] = []
): GuidanceTreeNode {
  const queue = eligibleWork.filter((item) => item.itemId);
  const eligibleIds = new Set(queue.map((item) => item.itemId));
  const queueById = new Map(queue.map((item) => [item.itemId, item]));

  const walk = (node: GuidanceTreeNode): GuidanceTreeNode | null => {
    if (node.type === 'activity') {
      if (!eligibleIds.has(node.id)) return null;
      const item = queueById.get(node.id);
      return item ? activityNodeFromEligibleQueue(item) : node;
    }

    const kids = (node.children ?? [])
      .map((child) => walk(child))
      .filter((child): child is GuidanceTreeNode => child != null);

    if (!keepEligibleOnlyBranch(node, kids.length)) {
      return null;
    }

    const counts =
      node.counts || isGuidanceNodeCollapsible(node) ? rollupFromChildren(kids) : undefined;

    return {
      ...node,
      children: kids.length > 0 ? kids : undefined,
      counts,
    };
  };

  const filtered = walk(root) ?? { ...root, children: [] };
  return appendMissingEligibleActivities(filtered, queue, activityNodeFromEligibleQueue);
}

/**
 * Inject attention-queue activities missing from the Guidance tree (default path).
 * Without this, empty waypoint shells hide eligible ACTIVE work when improvements
 * lack waypoint_ref (Fred gate-repair side effect).
 */
export function ensureEligibleActivitiesInTree(
  root: GuidanceTreeNode,
  eligibleWork: ControlBridgeQueueItem[] = []
): GuidanceTreeNode {
  const queue = eligibleWork.filter((item) => item.itemId);
  if (queue.length === 0) return root;
  return appendMissingEligibleActivities(root, queue, activityNodeFromEligibleQueue);
}

/** Collapse completed branches; keep active/todo branches expanded for first paint. */
export function initialGuidanceCollapsedState(root: GuidanceTreeNode): Record<string, boolean> {
  const collapsed: Record<string, boolean> = {};

  const walk = (node: GuidanceTreeNode) => {
    const kids = node.children ?? [];
    if (!isGuidanceNodeCollapsible(node)) {
      kids.forEach(walk);
      return;
    }

    const allChildrenDone = kids.length > 0 && kids.every((c) => c.status === 'done');
    if (node.status === 'done' || allChildrenDone) {
      collapsed[node.id] = true;
    }

    kids.forEach(walk);
  };

  walk(root);
  return collapsed;
}

/**
 * Guidance timeline is session + waypoints only
 * (`co-kfo-pia-guidance-waypoint-timeline-view-20261007`).
 * Improvements and activities stay off this surface.
 */
export function filterGuidanceTreeWaypointsOnly(root: GuidanceTreeNode): GuidanceTreeNode {
  const collectWaypoints = (node: GuidanceTreeNode): GuidanceTreeNode[] => {
    if (node.type === 'waypoint') {
      return [{ ...node, children: undefined }];
    }
    return (node.children ?? []).flatMap(collectWaypoints);
  };

  const waypoints =
    root.type === 'waypoint' ? [{ ...root, children: undefined }] : collectWaypoints(root);

  if (root.type !== 'session') {
    return {
      ...root,
      children: waypoints.length > 0 ? waypoints : undefined,
      counts: rollupFromChildren(waypoints),
    };
  }

  return {
    ...root,
    children: waypoints.length > 0 ? waypoints : undefined,
    counts: rollupFromChildren(waypoints),
  };
}
